/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {useRef} from 'react';
import isNil from 'lodash.isnil';
import {CVSAssetType} from '@ion/core/src/types/entities';
import {onHover, onSelect} from '../helpers/MapViewUtils';
import {UpdaterProps} from '../layers/AssetLayer';

export type LayerSelectionProps = {
  view: __esri.MapView,
  layerId: string | string[],
  updater?: (param: UpdaterProps) => void,
  onItemSelected: Function,
  onHoverItem: any,
  hoveredItem?: any,
  clusterSelected: (selectedClusterId: any) => void,
  showGraphicPopup: (selectedGraphic: any) => void,
  callback?: (attributes: CVSAssetType) => void,
  currentPopupGraphic?: any
}

export type OnItemSelectParams = {
  item: CVSAssetType;
  graphic: __esri.Graphic;
  coordinates: object;
  callback?: (attributes: CVSAssetType) => void,
  omit: boolean;
};

/**
 * @isSameItem
 *
 * Determines if the selected layer item is the hovered layer item, this utility
 * is intended to omit the hovered out state if the item is the selected item.
 */
const isSameItem = (selectionRef, hoverRef) => {
  if (selectionRef.current && hoverRef.current) {
    const {selectedItem} = selectionRef.current;
    const {hoveredItem} = hoverRef.current;
    return selectedItem?.id === hoveredItem?.id;
  }
};

/**
 * @switchCursor
 *
 * Changes the cursor to a pointer on hover, otherwise returns to the default cursor.
 */
export const switchCursor = (pointer = true) => {
  const elements = document.querySelectorAll('.esri-view-surface');
  elements.forEach(({style}) => {
    style.cursor = pointer ? 'pointer' : 'default';
  });
};

/**
 * @useLayerSelection
 *
 * Provides a layer specific hook which allows for managing the hover state and
 * selection state of items within a map. The hook requires a custom updater
 * function which will be invoked both on hover and on click of an item within
 * the layer.
 *
 * ```typescript
 *
 *    export const VehicleLayer = ({view, vehicles, onSelected}: Props) => {
 *      const initializeSelection = useLayerSelection(view,
 *        'vehicles',
 *         updater,
 *         onSelected
 *      );
 *      useEffect(() => {
 *        loadModules(['...'])
 *          .then(([...]) => {
 *            initializeSelection();
 *          });
 *        return function() {
 *          view.graphics.removeAll();
 *       };
 *     }, [view]);
 *     return null;
 *    };
 *
 * ```
 *
 */
export const useLayerSelection = ({
  view,
  layerId,
  updater,
  onItemSelected,
  onHoverItem,
  clusterSelected,
  showGraphicPopup,
  currentPopupGraphic,
  callback
}: LayerSelectionProps) => {
  const selectionRef = useRef(null);
  const hoverRef = useRef(null);

  // Defines a utility to reset the selection ref
  const onItemDeselect = () => {
    selectionRef.current = null;
  };

  // handles the item selected state within the given layer ...
  const onItemSelect = function({
    item,
    graphic,
    coordinates,
    callback,
    omit = false
  }: OnItemSelectParams) {
    if (item?.type && item?.type === 'Feature') {
      clusterSelected(item.id);
      return false;
    }
    if (!omit) {
      showGraphicPopup(graphic);
    }
    if (selectionRef.current) {
      const {selectedItem, selectedGraphic} = selectionRef.current;
      updater?.({asset: selectedItem, graphic: selectedGraphic, isHover: false, view});
    }
    updater?.({asset: item, graphic, isHover: true, view});
    selectionRef.current = {selectedItem: item, selectedGraphic: graphic};
    callback?.(item);
    !omit && onItemSelected(item, coordinates, view);
  };

  // handles the item hover state within the given layer ...
  const onItemHover = function(item: CVSAssetType, graphic: object) {
    const {hoveredItem, hoveredGraphic} = hoverRef?.current || {};
    if (hoverRef?.current?.hoveredItem) {
      const isSelected = isSameItem(selectionRef, hoverRef);
      if ((item && !isSelected) || !isSelected) {
        updater(
          {
            view,
            asset               : hoveredItem,
            graphic             : hoveredGraphic,
            isHover             : false,
            onHoveredPanelAsset : onHoverItem,
            hoveredAsset        : hoveredItem
          }
        );
      }
      if (isNil(item) && !isNil(hoveredItem) && !isSelected) {
        updater(
          {
            view,
            asset               : hoveredItem,
            graphic             : hoveredGraphic,
            isHover             : false,
            onHoveredPanelAsset : onHoverItem,
            hoveredAsset        : hoveredItem
          }
        );
      }
    }
    if ((item && graphic) && item.type !== 'Feature') {
      const {assetId} = currentPopupGraphic?.current?.attributes || {};
      updater({
        graphic,
        view,
        asset               : item,
        isHover             : true,
        onHoveredPanelAsset : onHoverItem,
        hoveredAsset        : hoveredItem,
        markerOnly          : assetId === item.assetId
      });
      switchCursor();
      hoverRef.current = {hoveredItem: item, hoveredGraphic: graphic};
    } else {
      switchCursor(false);
    }
  };

  const handler = (item, graphic, coordinates) => {
    onItemSelect({item, graphic, coordinates, callback, omit: false});
  };
  // provides the initialization function to be called when the map is ready ...
  const initializeSelection = function() {
    updater && onHover({view, layerId, handler: onItemHover, includeAny: true});
    onSelect({view, layerId, handler});
  };

  return {initializeSelection, onItemSelect, onItemDeselect, onItemHover};
};

export default useLayerSelection;
